import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { updateFavicon, updateTabTitle, requestNotificationPermission, showNotification, isTabVisible, playNotificationSound } from '../utils/browserNotifications';
import { AuthContext } from './AuthContext';

const ChatContext = createContext();

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};

export const ChatProvider = ({ children }) => {
  const { user, token } = useContext(AuthContext);
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);
  const [previousUnreadCount, setPreviousUnreadCount] = useState(0);
  const [notificationPermission, setNotificationPermission] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState({});

  // Request notification permission on mount
  useEffect(() => {
    const initNotifications = async () => {
      const hasPermission = await requestNotificationPermission();
      setNotificationPermission(hasPermission);
    };
    
    initNotifications();
  }, []);

  // Fetch unread counts when user is authenticated
  const fetchUnreadCounts = useCallback(async () => {
    if (!token || !user) return;
    
    try {
      const API_BASE = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE || 'https://intranet.nesimltd.co.ke';
      const response = await fetch(`${API_BASE}/api/chat/rooms/unread`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        
        const messages = {};
        data.forEach(item => {
          messages[item.roomId] = item.unreadCount;
        });
        
        setUnreadMessages(messages);
        
        // Calculate total unread count
        const totalUnread = Object.values(messages).reduce((sum, count) => sum + count, 0);
        setTotalUnreadCount(totalUnread);
      } else {
        console.error('ChatContext: Failed to fetch unread counts:', response.status);
        setUnreadMessages({});
        setTotalUnreadCount(0);
      }
    } catch (error) {
      console.error('ChatContext: Error fetching unread counts:', error);
      setUnreadMessages({});
      setTotalUnreadCount(0);
    }
  }, [token, user]);

  // Initialize unread counts when user is authenticated
  useEffect(() => {
    if (token && user) {
      fetchUnreadCounts();
    } else {
      setUnreadMessages({});
      setTotalUnreadCount(0);
    }
  }, [token, user, fetchUnreadCounts]);

  // Update browser indicators when unread count changes
  useEffect(() => {
    // Always update favicon and tab title regardless of notification permission
    updateFavicon(totalUnreadCount);
    updateTabTitle(totalUnreadCount);
    
    // Show notification and sound only if permission is granted
    if (totalUnreadCount > previousUnreadCount && notificationPermission) {
      const newMessages = totalUnreadCount - previousUnreadCount;
      const messageText = newMessages === 1 ? 'new message' : 'new messages';
      
      showNotification(
        `${newMessages} ${messageText}`,
        {
          body: `You have ${totalUnreadCount} unread messages in NESIM Intranet`,
          tag: 'nesim-chat-notification'
        }
      );
      
      // Play notification sound
      playNotificationSound();
    }
    
    setPreviousUnreadCount(totalUnreadCount);
  }, [totalUnreadCount, previousUnreadCount, notificationPermission]);

  const updateTotalUnreadCount = (count) => {
    setTotalUnreadCount(count);
  };

  const updateUnreadMessages = useCallback((messages) => {
    console.log('🔄 ChatContext: Updating unread messages:', messages);
    console.log('🔄 ChatContext: Total count:', Object.values(messages).reduce((sum, count) => sum + count, 0));
    setUnreadMessages(messages);
    const totalUnread = Object.values(messages).reduce((sum, count) => sum + count, 0);
    setTotalUnreadCount(totalUnread);
  }, []);

  // Increment unread count for a specific roomId using functional updates
  const incrementUnread = useCallback((roomId) => {
    if (!roomId) return;
    const normalizedId = String(roomId);
    setUnreadMessages(prev => {
      const next = { ...prev, [normalizedId]: (prev?.[normalizedId] || 0) + 1 };
      const totalUnread = Object.values(next).reduce((sum, count) => sum + count, 0);
      setTotalUnreadCount(totalUnread);
      return next;
    });
  }, []);

  const value = {
    totalUnreadCount,
    updateTotalUnreadCount,
    notificationPermission,
    unreadMessages,
    updateUnreadMessages,
    fetchUnreadCounts,
    incrementUnread
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
};
